/**
 * Custom hook for managing localStorage-persisted settings
 */

import { useState, useEffect, useCallback } from 'react';
import { STORAGE_KEYS, DEFAULT_SETTINGS } from 'constants/config';

export type ChatModeType = 'project' | 'legislation';

export function usePersistedSettings() {
  const [language, setLanguageState] = useState<string>(DEFAULT_SETTINGS.LANGUAGE);
  const [legislationCountry, setLegislationCountryState] = useState<string>(DEFAULT_SETTINGS.LEGISLATION_COUNTRY);
  const [validator, setValidatorState] = useState<string>(DEFAULT_SETTINGS.VALIDATOR);
  const [chatMode, setChatModeState] = useState<ChatModeType>(DEFAULT_SETTINGS.CHAT_MODE);
  // Opt-in validation debug capture (defaults OFF, boolean-only persistence).
  const [debugMode, setDebugModeState] = useState<boolean>(DEFAULT_SETTINGS.DEBUG_MODE);

  // Load from localStorage on mount
  useEffect(() => {
    const storedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    if (storedLanguage) {
      setLanguageState(storedLanguage);
    }

    const storedLegislationCountry = localStorage.getItem(STORAGE_KEYS.LEGISLATION_COUNTRY);
    if (storedLegislationCountry) {
      setLegislationCountryState(storedLegislationCountry);
    }

    const storedValidator = localStorage.getItem(STORAGE_KEYS.VALIDATOR);
    if (storedValidator) {
      setValidatorState(storedValidator);
    }

    const storedChatMode = localStorage.getItem(STORAGE_KEYS.CHAT_MODE);
    if (storedChatMode && (storedChatMode === 'project' || storedChatMode === 'legislation')) {
      setChatModeState(storedChatMode as ChatModeType);
    }

    // Boolean-only persistence; any stored value other than the literal
    // string 'true' is treated as OFF (fail-safe default).
    const storedDebugMode = localStorage.getItem(STORAGE_KEYS.DEBUG_MODE);
    setDebugModeState(storedDebugMode === 'true');
  }, []);

  const setLanguage = useCallback((value: string) => {
    setLanguageState(value);
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, value);
  }, []);

  const setLegislationCountry = useCallback((value: string) => {
    setLegislationCountryState(value);
    localStorage.setItem(STORAGE_KEYS.LEGISLATION_COUNTRY, value);
  }, []);

  const setValidator = useCallback((value: string) => {
    setValidatorState(value);
    localStorage.setItem(STORAGE_KEYS.VALIDATOR, value);
  }, []);

  const setChatMode = useCallback((value: ChatModeType) => {
    setChatModeState(value);
    localStorage.setItem(STORAGE_KEYS.CHAT_MODE, value);
  }, []);

  const setDebugMode = useCallback((value: boolean) => {
    setDebugModeState(value);
    localStorage.setItem(STORAGE_KEYS.DEBUG_MODE, value ? 'true' : 'false');
  }, []);

  return {
    language,
    legislationCountry,
    validator,
    chatMode,
    debugMode,
    setLanguage,
    setLegislationCountry,
    setValidator,
    setChatMode,
    setDebugMode
  };
}
